import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import {
  defaultHolidayPayCalcOptions,
  holidayPaymentMethodOptions,
  yesNoNAOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import { HolidayMethodEnum, YesNoNAEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Col, Form, Row } from 'antd';
import React, { Ref, useEffect, useImperativeHandle } from 'react';
import AppPercentageInput from '@nxsys/components/AppPercentageInput';

export type PayrollSettingFormValues = {
  timesheetApprovalNeeded: YesNoNAEnum | null;
  listEmprApprovers: Option<string>[] | null;
  payrollApproveNeeded: YesNoNAEnum | null;
  enableHolidayPay: boolean;
  payeHolidayPayRate: string;
  holidayPayMethod: Option<string> | null;
  holidayPayCalculationRule: Option<string> | null;
  includeInTimesheetRate: boolean | null;
};

export type PayrollSettingFormRef = {
  submit: () => void;
};

type PayrollSettingFormProps = {
  formRef?: Ref<PayrollSettingFormRef>;
  onSubmit?: (values: PayrollSettingFormValues) => void;
  defaultValues?: Partial<PayrollSettingFormValues>;
  companyId?: string;
};

const PayrollSettingForm: React.FC<PayrollSettingFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  companyId,
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm<PayrollSettingFormValues>();
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  useEffect(() => {
    if (
      watchValues?.holidayPayMethod?.value ===
      HolidayMethodEnum.FIFTY_TWO_WEEK_AVERAGE
    ) {
      const allEarnings = defaultHolidayPayCalcOptions.find(
        (opt) => opt.value === 'ALL_EARNINGS',
      );
      if (allEarnings) {
        form.setFieldValue('holidayPayCalculationRule', allEarnings);
      }
      form.setFieldValue('includeInTimesheetRate', true);
    }
  }, [watchValues?.holidayPayMethod?.value]);

  const onFinish = (values: PayrollSettingFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      <Row gutter={[16, 0]}>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.holidayPay']}
            name="enableHolidayPay"
          >
            <AppRadioGroup options={yesNoOptions} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={`${messages['common.holidayPayRate']} %`}
            name="payeHolidayPayRate"
          >
            <AppPercentageInput
              placeholder={messages['form.enter']}
              disabled={!watchValues?.enableHolidayPay}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.holidayPayMethod']}
            name="holidayPayMethod"
          >
            <AppSelect
              options={holidayPaymentMethodOptions}
              placeholder={messages['form.select']}
              disabled={!watchValues?.enableHolidayPay}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.holidayPayCalculationRule']}
            name="holidayPayCalculationRule"
          >
            <AppSelect
              options={defaultHolidayPayCalcOptions}
              placeholder={messages['form.select']}
              disabled={!watchValues?.enableHolidayPay}
            />
          </Form.Item>
        </Col>
        {watchValues?.holidayPayMethod &&
          watchValues.holidayPayMethod.value !==
            HolidayMethodEnum.NO_HOLIDAY_PAY && (
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.includedTimesheetRate']}
                name="includeInTimesheetRate"
              >
                <AppRadioGroup
                  options={yesNoOptions}
                  disabled={!watchValues?.enableHolidayPay}
                />
              </Form.Item>
            </Col>
          )}
      </Row>
      <Row gutter={16}>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.timesheetApprovalNeeded']}
            name="timesheetApprovalNeeded"
            valuePropName="value"
          >
            <AppRadioGroup options={yesNoNAOptions} />
          </Form.Item>
        </Col>
        {watchValues?.timesheetApprovalNeeded === YesNoNAEnum.YES && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.timesheetApprover']}
              name="listEmprApprovers"
            >
              <AppSelectSync
                type="approver"
                apiType="searchUserAdminCompany"
                placeholder={messages['form.select']}
                style={{ width: '100%' }}
                onChangeValue={(value: any) => {
                  form.setFieldValue('listEmprApprovers', value);
                }}
                mode="multiple"
                paramFilter={{
                  companyUserIds: [companyId],
                  roles: ['company'],
                }}
              />
            </Form.Item>
          </Col>
        )}
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.payrollApprovalNeeded']}
            name="payrollApproveNeeded"
            valuePropName="value"
          >
            <AppRadioGroup options={yesNoNAOptions} />
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default PayrollSettingForm;
